import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, Select, MenuItem, FormControl, Chip } from "@mui/material";
import { CalendarToday as CalendarIcon, PictureAsPdf as PdfIcon } from "@mui/icons-material";
import { FolderOpen, FileText, Shield, CreditCard } from "lucide-react";
import LoaderIndicator from "../../Utility/loading";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Cell,
  LabelList,
  PieChart,
  Pie,
} from "recharts";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { handleLogout } from "../../Utility/utility";
import { BASE_URL } from "../../App_Settings/app_settings";
import {
  getUserIdFromLocalStorage,
  getUserFileAuditByUserId,
} from "../../API_Endpoints/APIEndpoints";
import "./dashboard.css";
import { screenHeaderStyle, useSharedValue } from "../Constants/constants";
import { useHistory } from "react-router-dom";

// ── Date Range Preset Helpers ──
const getDateRange = (preset) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case "today":
      return { start: today, end: now };
    case "7days": {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      return { start, end: now };
    }
    case "30days": {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      return { start, end: now };
    }
    case "thisMonth":
      return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: now };
    case "thisYear":
      return { start: new Date(now.getFullYear(), 0, 1), end: now };
    case "all":
    default:
      return { start: null, end: null };
  }
};

const DATE_PRESETS = [
  { value: "all", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "7days", label: "Last 7 Days" },
  { value: "30days", label: "Last 30 Days" },
  { value: "thisMonth", label: "This Month" },
  { value: "thisYear", label: "This Year" },
];

// ── Custom Tooltip ──
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p>{`${payload[0].payload.name}: ${payload[0].value}`}</p>
      </div>
    );
  }
  return null;
};

// ── Document Type Stat Card ──
const DocTypeCard = ({ icon, label, uploaded, extracted, color, bgColor }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      backgroundColor: "#fff",
      padding: "16px 20px",
      borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
      flex: 1,
      minWidth: 0,
      border: "1px solid #f0f0f0",
      transition: "box-shadow 0.2s ease, transform 0.2s ease",
      gap: "12px",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
      e.currentTarget.style.transform = "translateY(-1px)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)";
      e.currentTarget.style.transform = "translateY(0)";
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: "8px",
          backgroundColor: bgColor || "#f0f7ff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {React.isValidElement(icon)
          ? React.cloneElement(icon, { style: { fontSize: 18, color: color || "#2aa7e0" } })
          : typeof icon === "function" || (typeof icon === "object" && icon !== null)
            ? React.createElement(icon, { size: 18, color: color || "#2aa7e0" })
            : null}
      </div>
      <span
        style={{
          fontSize: "13px",
          fontWeight: 600,
          color: "#374151",
          letterSpacing: "0.2px",
        }}
      >
        {label}
      </span>
    </div>
    <div style={{ display: "flex", gap: "12px" }}>
      <div
        style={{
          flex: 1,
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          padding: "8px 12px",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "11px", color: "#8c8c8c", fontWeight: 500, marginBottom: "2px" }}>
          Uploaded
        </div>
        <div style={{ fontSize: "20px", fontWeight: 700, color: "#1a1a1a", lineHeight: 1.2 }}>
          {uploaded ?? "—"}
        </div>
      </div>
      <div
        style={{
          flex: 1,
          backgroundColor: bgColor || "#f0f7ff",
          borderRadius: "8px",
          padding: "8px 12px",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "11px", color: "#8c8c8c", fontWeight: 500, marginBottom: "2px" }}>
          Extracted
        </div>
        <div
          style={{ fontSize: "20px", fontWeight: 700, color: color || "#2aa7e0", lineHeight: 1.2 }}
        >
          {extracted ?? "—"}
        </div>
      </div>
    </div>
  </div>
);

// ── Activity Badge ──
const ActivityBadge = ({ label, date, icon }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "6px 14px",
      backgroundColor: "#f8f9fa",
      borderRadius: "20px",
      border: "1px solid #e9ecef",
    }}
  >
    {icon && <img src={icon} alt={label} style={{ width: "16px", height: "16px", opacity: 0.7 }} />}
    <span style={{ fontSize: "11px", color: "#8c8c8c", fontWeight: 500 }}>{label}</span>
    <span style={{ fontSize: "12px", color: "#333", fontWeight: 600 }}>{date || "N/A"}</span>
  </div>
);

export default function DashboardPage() {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [datePreset, setDatePreset] = useState("all");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const { defaultBusinessunit, defaultOrganization, defaultUser, isMenuOpen } = useSharedValue();

  // Dashboard activity timestamps
  const [lastExtracted, setLastExtracted] = useState(null);
  const [lastImported, setLastImported] = useState(null);
  const [lastExported, setLastExported] = useState(null);

  // Chart data
  const [fileProcessingFunnelData, setFileProcessingFunnelData] = useState([]);
  const [workflowStatusData, setWorkflowStatusData] = useState([]);
  const [fileLifecycleData, setFileLifecycleData] = useState([]);

  // Document type breakdown
  const [docTypeBreakdown, setDocTypeBreakdown] = useState({
    packsUploaded: 0,
    packsExtracted: 0,
    formsUploaded: 0,
    formsExtracted: 0,
    whsUploaded: 0,
    whsExtracted: 0,
    idDocsUploaded: 0,
    idDocsExtracted: 0,
  });

  // Refs
  const prevFiltersRef = useRef({
    businessunit: defaultBusinessunit,
    organization: defaultOrganization,
    user: defaultUser,
  });

  const formatStoredDate = (dateString) => {
    const date = dateString ? new Date(dateString) : new Date();
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  // Handle date preset change
  const handleDatePresetChange = useCallback((event) => {
    const preset = event.target.value;
    setDatePreset(preset);
    const { start, end } = getDateRange(preset);
    setStartDate(start);
    setEndDate(end);
  }, []);

  // Fetch dashboard data
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!isMounted) return;

      try {
        setLoading(true);
        const token = getUserIdFromLocalStorage().token;

        const params = new URLSearchParams();
        if (defaultUser && defaultUser.userId) {
          params.set("userId", defaultUser.userId);
        } else if (defaultUser && defaultUser.id) {
          params.set("userId", defaultUser.id);
        } else if (defaultOrganization && defaultOrganization.organizationId) {
          params.set("organizationId", defaultOrganization.organizationId);
        } else if (defaultOrganization && defaultOrganization.id) {
          params.set("organizationId", defaultOrganization.id);
        }

        if (startDate) {
          params.set("startDate", new Date(startDate).toISOString());
        }
        if (endDate) {
          params.set("endDate", new Date(endDate).toISOString());
        }

        const queryString = params.toString();
        const dashboardUrl = `${BASE_URL}/Dashboard/summary${queryString ? `?${queryString}` : ""}`;

        const response = await fetch(dashboardUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });

        if (response.status === 401) {
          handleLogout(history);
          return;
        }

        if (!response.ok) {
          console.error("Dashboard API error:", response.status);
          return;
        }

        const data = await response.json();
        if (!isMounted) return;

        // Extraction Funnel (Bar Chart)
        const funnelData = [
          { name: "Uploaded", value: data.totalUploaded ?? 0, color: "#4C96D7" },
          { name: "Waiting", value: data.totalWaiting ?? 0, color: "#4CBBB0" },
          { name: "Extracted", value: data.totalExtracted ?? 0, color: "#F3C24A" },
          { name: "Approved", value: data.totalApproved ?? 0, color: "#D988BA" },
          { name: "Rejected", value: data.totalRejected ?? 0, color: "#7813d1" },
        ];
        setFileProcessingFunnelData(funnelData);
        setFileLifecycleData(funnelData);

        // Forms Statistics (Pie Chart)
        const formsChartData = [
          { name: "Pages", value: data.totalPages ?? 0, color: "#8B5CF6" },
          { name: "Forms", value: data.totalForms ?? 0, color: "#EC4899" },
          { name: "ID Documents", value: data.idDocuments ?? 0, color: "#3B82F6" },
          {
            name: "Withholding Statements",
            value: data.withholdingStatements ?? 0,
            color: "#10B981",
          },
          { name: "Other", value: data.otherDocuments ?? 0, color: "#F59E0B" },
        ];
        setWorkflowStatusData(formsChartData);

        // Activity Timestamps
        setLastImported(data.lastImported ? formatStoredDate(data.lastImported) : null);
        setLastExtracted(data.lastExtracted ? formatStoredDate(data.lastExtracted) : null);
        setLastExported(data.lastExported ? formatStoredDate(data.lastExported) : null);

        // Document type breakdown (Stat Cards)
        setDocTypeBreakdown({
          packsUploaded: data.packsUploaded ?? 0,
          packsExtracted: data.packsExtracted ?? 0,
          formsUploaded: data.formsUploaded ?? 0,
          formsExtracted: data.formsExtracted ?? 0,
          whsUploaded: data.whsUploaded ?? 0,
          whsExtracted: data.whsExtracted ?? 0,
          idDocsUploaded: data.idDocsUploaded ?? 0,
          idDocsExtracted: data.idDocsExtracted ?? 0,
        });

        // Document Type Breakdown
        setDocTypeBreakdown({
          packsUploaded: data.packsUploaded ?? 0,
          packsExtracted: data.packsExtracted ?? 0,
          formsUploaded: data.formsUploaded ?? 0,
          formsExtracted: data.formsExtracted ?? 0,
          whsUploaded: data.whsUploaded ?? 0,
          whsExtracted: data.whsExtracted ?? 0,
          idDocsUploaded: data.idDocsUploaded ?? 0,
          idDocsExtracted: data.idDocsExtracted ?? 0,
        });
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        if (error.message && error.message.includes("401")) {
          handleLogout(history);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    prevFiltersRef.current = {
      businessunit: defaultBusinessunit,
      organization: defaultOrganization,
      user: defaultUser,
    };

    return () => {
      isMounted = false;
    };
  }, [defaultBusinessunit, defaultOrganization, defaultUser, startDate, endDate, history]);

  // PDF Report download
  const handleDownloadPDFReport = () => {
    setIsLoading(true);

    getUserFileAuditByUserId()
      .then((data) => {
        let filteredData = data;
        if (startDate && endDate) {
          filteredData = data.filter((item) => {
            const itemDate = new Date(item.createdDate);
            const startDateTime = new Date(startDate).setHours(0, 0, 0, 0);
            const endDateTime = new Date(endDate).setHours(23, 59, 59, 999);
            return itemDate >= startDateTime && itemDate <= endDateTime;
          });
        }

        const doc = generatePDF(filteredData, "Dashboard Report");
        doc.save(`dashboard-report-${new Date().toISOString().split("T")[0]}.pdf`);
        localStorage.setItem("downloadTime", new Date().toISOString());
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error generating PDF report:", error);
        setIsLoading(false);
      });
  };

  const generatePDF = (data, reportName) => {
    const doc = new jsPDF();

    const logoPath = "/logo.png";
    doc.addImage(logoPath, "PNG", 14, 10, 50, 9);

    doc.setFontSize(15);
    doc.text(reportName, 14, 25);

    if (startDate && endDate) {
      const formattedStartDate = new Date(startDate).toLocaleDateString();
      const formattedEndDate = new Date(endDate).toLocaleDateString();
      doc.setFontSize(10);
      doc.text(`Date Range: ${formattedStartDate} to ${formattedEndDate}`, 14, 30);
    }

    const userInfo = getUserIdFromLocalStorage();
    const userName = `${userInfo.firstName} ${userInfo.lastName}` || "N/A";
    const userEmail = userInfo?.userName || "N/A";
    const organizationName = userInfo?.organizationName || "N/A";

    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 35);
    doc.text(`User: ${userName}`, 14, 40);
    doc.text(`Email: ${userEmail}`, 14, 45);
    doc.text(`Organization: ${organizationName}`, 14, 50);

    doc.setFontSize(12);
    doc.text("Extraction Statistics", 14, 65);
    doc.autoTable({
      head: [["Stage", "Value"]],
      body: fileProcessingFunnelData.map((item) => [item.name, item.value]),
      startY: 70,
      theme: "grid",
      headStyles: { fillColor: [42, 167, 224] },
      styles: { fontSize: 8 },
    });

    doc.setFontSize(12);
    doc.text("Forms Statistics", 14, doc.autoTable.previous.finalY + 15);
    doc.autoTable({
      head: [["Form Type", "Count"]],
      body: workflowStatusData.map((item) => [item.name, item.value]),
      startY: doc.autoTable.previous.finalY + 20,
      theme: "grid",
      headStyles: { fillColor: [42, 167, 224] },
      styles: { fontSize: 8 },
    });

    doc.text("Total Statistics", 14, doc.autoTable.previous.finalY + 15);
    doc.autoTable({
      head: [["Document Type", "Uploaded", "Extracted"]],
      body: [
        ["Packs", docTypeBreakdown.packsUploaded, docTypeBreakdown.packsExtracted],
        ["Forms", docTypeBreakdown.formsUploaded, docTypeBreakdown.formsExtracted],
        ["Withholding Statements", docTypeBreakdown.whsUploaded, docTypeBreakdown.whsExtracted],
        ["ID Documents", docTypeBreakdown.idDocsUploaded, docTypeBreakdown.idDocsExtracted],
      ],
      startY: doc.autoTable.previous.finalY + 20,
      theme: "grid",
      headStyles: { fillColor: [42, 167, 224] },
      styles: { fontSize: 8 },
    });

    return doc;
  };

  // Compute totals for stat cards from document type breakdown
  const {
    packsUploaded,
    packsExtracted,
    formsUploaded,
    formsExtracted,
    whsUploaded,
    whsExtracted,
    idDocsUploaded,
    idDocsExtracted,
  } = docTypeBreakdown;

  const userInfo = getUserIdFromLocalStorage();
  const userName = `${userInfo.firstName || ""} ${userInfo.lastName || ""}`.trim();
  const selectedPresetLabel = DATE_PRESETS.find((p) => p.value === datePreset)?.label || "All Time";

  return (
    <div
      style={{
        ...screenHeaderStyle(isMenuOpen),
        overflow: "hidden",
        height: "100vh",
        backgroundColor: "#f0f2f5",
      }}
    >
      <div style={{ height: "100%", overflow: "auto", padding: "20px 24px" }}>
        {/* ── Top Bar: Title + Date Selector + Actions ── */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <div>
            <h1
              style={{
                fontSize: "22px",
                fontWeight: 700,
                color: "#1a1a1a",
                margin: 0,
                lineHeight: 1.3,
              }}
            >
              Dashboard
            </h1>
            <span style={{ fontSize: "13px", color: "#8c8c8c", fontWeight: 400 }}>
              Welcome back, {userName || "User"}
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {/* Activity Badges */}
            <ActivityBadge label="Imported" date={lastImported} icon="/extract.svg" />
            <ActivityBadge label="Extracted" date={lastExtracted} icon="/extract.svg" />
            <ActivityBadge label="Exported" date={lastExported} icon="/import.svg" />

            {/* Date Range Selector */}
            <FormControl size="small">
              <Select
                value={datePreset}
                onChange={handleDatePresetChange}
                displayEmpty
                startAdornment={
                  <CalendarIcon style={{ fontSize: 18, color: "#8c8c8c", marginRight: 6 }} />
                }
                sx={{
                  minWidth: 170,
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: 500,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#2aa7e0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#2aa7e0",
                  },
                }}
              >
                {DATE_PRESETS.map((preset) => (
                  <MenuItem key={preset.value} value={preset.value} sx={{ fontSize: "13px" }}>
                    {preset.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Download Report */}
            <Button
              variant="contained"
              onClick={handleDownloadPDFReport}
              disabled={isLoading}
              startIcon={<PdfIcon style={{ fontSize: 18 }} />}
              sx={{
                backgroundColor: "#2aa7e0",
                color: "#fff",
                fontWeight: 600,
                borderRadius: "8px",
                textTransform: "none",
                padding: "7px 18px",
                fontSize: "13px",
                height: "40px",
                boxShadow: "0 2px 6px rgba(42,167,224,0.3)",
                "&:hover": {
                  backgroundColor: "#1e8fc4",
                  boxShadow: "0 4px 12px rgba(42,167,224,0.4)",
                },
                "&:disabled": {
                  backgroundColor: "#b0d9ef",
                },
              }}
            >
              {isLoading ? "Generating..." : "Export PDF"}
            </Button>
          </div>
        </div>

        {/* ── Document Type Breakdown Cards ── */}
        <div style={{ display: "flex", gap: "16px", marginBottom: "20px" }}>
          <DocTypeCard
            icon={FolderOpen}
            label="Packs"
            uploaded={loading ? "..." : packsUploaded}
            extracted={loading ? "..." : packsExtracted}
            color="#4C96D7"
            bgColor="#eef5fc"
          />
          <DocTypeCard
            icon={FileText}
            label="Forms"
            uploaded={loading ? "..." : formsUploaded}
            extracted={loading ? "..." : formsExtracted}
            color="#e6a817"
            bgColor="#fdf6e3"
          />
          <DocTypeCard
            icon={Shield}
            label="Withholding Statements"
            uploaded={loading ? "..." : whsUploaded}
            extracted={loading ? "..." : whsExtracted}
            color="#10B981"
            bgColor="#ecfdf5"
          />
          <DocTypeCard
            icon={CreditCard}
            label="ID Documents"
            uploaded={loading ? "..." : idDocsUploaded}
            extracted={loading ? "..." : idDocsExtracted}
            color="#8B5CF6"
            bgColor="#f3ecfb"
          />
        </div>

        {/* ── Active Filter Chip ── */}
        {datePreset !== "all" && (
          <div style={{ marginBottom: "16px" }}>
            <Chip
              label={`Filtered: ${selectedPresetLabel}`}
              size="small"
              onDelete={() => {
                setDatePreset("all");
                setStartDate(null);
                setEndDate(null);
              }}
              sx={{
                backgroundColor: "#e3f2fd",
                color: "#1565c0",
                fontWeight: 500,
                fontSize: "12px",
                "& .MuiChip-deleteIcon": { color: "#1565c0" },
              }}
            />
          </div>
        )}

        {/* ── Charts Grid ── */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "20px",
          }}
        >
          {/* Extraction Funnel Bar Chart */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
              border: "1px solid #f0f0f0",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "16px 20px 8px",
                borderBottom: "1px solid #f5f5f5",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3
                style={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "#1a1a1a",
                  margin: 0,
                }}
              >
                File Processing Overview
              </h3>
              <span style={{ fontSize: "11px", color: "#aaa", fontWeight: 500 }}>
                {selectedPresetLabel}
              </span>
            </div>
            <div
              style={{
                padding: "12px 8px",
                height: 420,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {loading ? (
                <LoaderIndicator />
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={fileProcessingFunnelData}
                    margin={{ top: 20, right: 20, left: 10, bottom: 8 }}
                    barSize={52}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      horizontal={true}
                      vertical={false}
                      stroke="#f0f0f0"
                    />
                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: "#888" }}
                      interval={0}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: "#aaa" }}
                    />
                    <RechartsTooltip
                      content={<CustomTooltip />}
                      cursor={{ fill: "rgba(0,0,0,0.03)" }}
                    />
                    <Bar
                      dataKey="value"
                      radius={[6, 6, 0, 0]}
                      isAnimationActive={true}
                      animationDuration={800}
                    >
                      {fileProcessingFunnelData.map((entry, index) => (
                        <Cell key={`bar-${index}`} fill={entry.color} />
                      ))}
                      <LabelList
                        dataKey="value"
                        position="top"
                        style={{ fontSize: "12px", fontWeight: "bold", fill: "#555" }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Forms Breakdown Donut Chart */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
              border: "1px solid #f0f0f0",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "16px 20px 8px",
                borderBottom: "1px solid #f5f5f5",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3
                style={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "#1a1a1a",
                  margin: 0,
                }}
              >
                Document Classification
              </h3>
              <span style={{ fontSize: "11px", color: "#aaa", fontWeight: 500 }}>
                {selectedPresetLabel}
              </span>
            </div>
            <div
              style={{
                padding: "12px 8px",
                height: 420,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {loading ? (
                <LoaderIndicator />
              ) : (
                <div style={{ display: "flex", width: "100%", height: 400, alignItems: "center" }}>
                  {/* Donut */}
                  <div style={{ position: "relative", width: "55%", height: "100%" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={workflowStatusData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          animationDuration={800}
                          stroke="none"
                        >
                          {workflowStatusData.map((entry, index) => (
                            <Cell key={`pie-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Center Label */}
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        textAlign: "center",
                        pointerEvents: "none",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "#aaa",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Total
                      </div>
                      <div
                        style={{
                          fontSize: "28px",
                          fontWeight: 700,
                          color: "#1a1a1a",
                          lineHeight: 1.1,
                        }}
                      >
                        {workflowStatusData.reduce((sum, item) => sum + item.value, 0)}
                      </div>
                    </div>
                  </div>

                  {/* Legend */}
                  <div
                    style={{
                      width: "45%",
                      paddingLeft: "16px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      gap: "10px",
                    }}
                  >
                    {workflowStatusData.map((entry, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          fontSize: "13px",
                          padding: "6px 10px",
                          borderRadius: "8px",
                          backgroundColor: "#fafafa",
                          transition: "background-color 0.15s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f0f0f0";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#fafafa";
                        }}
                      >
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            backgroundColor: entry.color,
                            marginRight: 10,
                            borderRadius: "50%",
                            flexShrink: 0,
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                            alignItems: "center",
                          }}
                        >
                          <span style={{ color: "#666", fontWeight: 500 }}>{entry.name}</span>
                          <span style={{ fontWeight: 700, color: "#333", marginLeft: 8 }}>
                            {entry.value}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
